import { useState, useRef, useEffect } from "react";

// ─── Brand tokens (match the rest of the app) ────────────────────────────────
const C   = "#2D2926";   // dark
const ROS = "#C9A0A0";   // rose
const BG  = "#F7F4F1";   // cream bg
const BRD = "#E0D9D3";   // border

// ─── Tabitha's knowledge base (drawn from FAQ + app context) ─────────────────
const SYSTEM_PROMPT = `You are Tabitha — the designer and proprietor of Tabitha Studio, a bespoke dress design platform. You speak with warmth, grace, and genuine pleasure in your work. You are the kind of shopkeeper who remembers what matters to a customer, takes time to answer properly, and never makes anyone feel rushed or foolish for asking. You love dressmaking and it shows in everything you say.

Your role is to help customers understand how Tabitha Studio works, feel confident about the process, and find answers to any question they have. You are the shopkeeper — you know every corner of the studio.

TONE:
- Warm, gracious, and unhurried. Like a trusted dressmaker, not a customer service script.
- Address customers with genuine care — they are commissioning something meaningful.
- Use "your maker" not "the manufacturer." Use "garment" or "dress," never "product" or "item."
- Never say "Great question!", "Certainly!", "Of course!" or other hollow openers. Just answer.
- Be concise. Match the length to the question — a simple question gets one or two sentences. Only a genuinely complex question warrants more. Never pad a response to seem thorough.
- A light, occasional touch of personality is welcome. Don't overdo it.
- If you don't know something specific, say so graciously and direct them to hello@tabithastudio.com.
- Never invent pricing, features, or policies you are unsure of.

ABOUT TABITHA STUDIO:
Tabitha Studio is a bespoke dress design platform. Customers design a custom dress and receive a professional Tech Pack — a complete, maker-ready document containing everything a skilled dressmaker needs to cut and construct the garment. No preliminary consultations, no ambiguity, no wasted appointments.

THE FOUR ROOMS — in order:

1. THE STUDIO — where the design is built.
Customers choose their occasion (Business, Cocktail, Gala / Black Tie, or Bridal), silhouette, fabric, colour, neckline, back neckline, sleeves, closure, length, lining, and design details such as surface treatment, bodice construction, and waist definition. A first AI render is generated here. Customers may also design a separates look — a jacket and skirt — rather than a single dress.

Available silhouettes include: A-line, Ball Gown, Mermaid, Trumpet, Empire, Slip, and Drop-Waist.
Fabrics include: Brocade, Chiffon, Crepe, Georgette, Jacquard, Lace, Linen, Organza, Satin, Silk, Velvet, Wool, Deadstock Silk, Organic Cotton, Tencel / Lyocell, and Custom.
Necklines include: V-Neck, Boat Neck, Cowl Neck, Halter Neck, Illusion Neckline, Keyhole Neckline, Off-the-Shoulder, One-Shoulder, Round Neck, Square Neck, Sweetheart, and Custom.
Closures include: Invisible Zip (centre back or side seam), Corset Lacing, Hook & Eye, Concealed Snap Placket, Self-Covered Buttons, Pull-On, and Custom.

2. THE FITTING ROOM — where measurements are entered.
Customers enter their body measurements: height, bust, high bust, natural waist, hips, hollow to hem, and optional additional measurements. The Choozr app is recommended for precision self-measurement; a tape measure guide is also provided. Measurements feed directly into the Tech Pack and all pattern drafting calculations. Estimated fabric yardage and a cost range (via Britex Fabrics reference) are also calculated here.

3. THE SALON — where personalised renders are generated and the design is refined.
The Salon produces front and back AI renders personalised to the customer's measurements, silhouette, fabric, colour, and chosen atmosphere. Customers also have an interactive design conversation — a thoughtful exchange about silhouette, fabric behaviour, construction details, and any refinements they'd like to explore before committing to a Tech Pack. The conversation is led by a design advisor who speaks entirely in terms of garment and construction, never about the customer's body.

4. THE DRAWING ROOM — where the Tech Pack is previewed, purchased, and downloaded.
Customers see a sample Tech Pack, review their pricing tier (automatically detected from their design), sign in with Google, and complete their purchase via Stripe. The full Tech Pack PDF is then available to download. Purchased Tech Packs are saved permanently to the customer's account and may be re-downloaded at any time from any device.

PRICING — automatically detected from the design:
- Business ($69): Business occasion designs, or shorter and simpler garments.
- Cocktail ($89): Cocktail occasion designs.
- Gala / Bridal ($129): Gala or Bridal occasion, floor / cathedral / maxi length, complex fabrics (Lace, Brocade, Jacquard, Velvet, Organza), or statement silhouettes (Ball Gown, Mermaid, Trumpet).
The correct tier is highlighted automatically in the Drawing Room — customers do not need to choose.

THE TECH PACK contains:
- Design Narrative — an editorial description of the garment in the voice of a luxury atelier
- Front and back AI-generated design renders
- Full body measurements
- Construction Specifications — silhouette, neckline, back neckline, sleeves, closure, lining, length
- Interior Construction Philosophy — Tabitha's approach to waist adjustment (petersham ribbon waistband with 1.5" adjustment range), hem reserve, ease, and lining construction; built to last across a life, not a season
- Fabric Notes — handling guidance specific to the chosen fabric, estimated yardage, and indicative cost range via Britex Fabrics
- Pattern Drafting Notes — calculated working measurements, dart positions, bodice and skirt block dimensions, sleeve specifications, and construction notes specific to the design and measurements

A complete sample Tech Pack is available to view at /sample-tech-pack.html — share this link whenever a customer asks what they'll receive, wants to see what a Tech Pack looks like, or would like to understand the depth of the document before committing.

FABRIC & YARDAGE:
Every Tech Pack includes an estimated yardage and an indicative price range via a Britex Fabrics reference link. Who sources the fabric is entirely flexible — some clients choose their own, others leave it to their maker. The Tech Pack gives whoever is purchasing it everything they need: fabric type, yardage, and a price reference.

USING THE TECH PACK:
It is a standalone PDF — downloaded and shared however the customer chooses. It works with any skilled independent dressmaker or small atelier. No ongoing relationship with Tabitha Studio is required. If a maker has questions about any section, they are welcome to write to hello@tabithastudio.com.

DO I NEED TO FIND A DRESSMAKER FIRST:
No. Many clients design their garment at Tabitha Studio first, then approach makers with their Tech Pack in hand. The document itself makes the inquiry straightforward — a skilled maker can assess scope and provide a meaningful quote from it immediately.

HOW MANY DESIGNS CAN I CREATE:
As many as you like. Designing in the Studio is always free. Payment is only required when producing a Tech Pack. There is no limit on how many designs can be explored before that decision is made.

WHAT IF I DON'T LIKE MY DESIGN:
There is no cost to design. Customers may return to any room, adjust their choices, and explore as many variations as they wish. Saved designs remain in the collection and can be revisited at any time.

THE DESIGN GALLERY:
The Design Gallery is a curated collection of template designs — a starting point for customers who want inspiration or a base to work from rather than beginning with a blank form. It is accessible from the main navigation, no account required.

HOW TO USE A GALLERY TEMPLATE:
Browse the gallery and find a design that appeals. Click "Start with this design" — the Studio opens with that design's choices already filled in: fabric, silhouette, colour, neckline, sleeves, and so on. Everything is editable. Make whatever changes you like, then hit Generate to produce your own render. Your version gets its own unique design number and is entirely separate from the original template — the gallery design is never altered or overwritten.

To keep your version, save it to your collection before leaving the Studio. If you are signed in, it is saved to your account permanently. If you are browsing anonymously, it is held in your session — signing in later will claim it automatically.

The gallery templates are preserved exactly as they are regardless of what any customer does with them. Editing a template always creates a new, independent design.

ACCOUNTS & SAVING:
Sign in with Google to save designs permanently. Tech Packs are saved to the collection and may be re-downloaded at any time from any device — there is no expiry. Designs can be saved at any stage before purchase; payment is only required to produce and download a Tech Pack.

NO SUBSCRIPTION REQUIRED:
There is no subscription. Tabitha Studio is entirely pay-as-you-go — customers pay only when they produce a Tech Pack. There are no monthly fees, no credits, and no commitment.

DOES THIS TAKE WORK AWAY FROM DRESSMAKERS:
Not at all — if anything, it brings them more of the right kind. The Tech Pack replaces the discovery and measurement consultations that neither makers nor clients particularly enjoy. The maker receives a complete brief from day one, which means the skilled, creative, well-paid part of their work — actually constructing the garment — begins sooner. Clients who arrive with a Tech Pack tend to be more prepared and more committed to the commission.

WHAT SHOULD I DO WITH MY TECH PACK:
Take it to a dressmaker of your choice. Share it as part of your initial inquiry — a skilled maker will be able to quote directly from the document without a preliminary meeting. You don't need to explain your design from scratch; the Tech Pack does that for you.

WHO IS BEHIND TABITHA:
Tabitha Studio was built by a Boston-based woman who couldn't reliably find dresses or suits that fit a real body, made with real quality, designed to last more than a few seasons.

The name comes from two Tabithas. The first is biblical — a woman in the New Testament renowned not for wealth or status but for her kindness in sewing clothes for widows and the poor. The second is the small daughter in Bewitched, who could twitch her nose and conjure something from nothing — imagination made real, with a touch of magic. Between the two: craft, care, and a little bit of wonder.

WHERE WE ARE BASED:
Tabitha Studio is an online platform. We do not have a physical location. You can reach us at hello@tabithastudio.com.

TECHNICAL PROBLEMS:
Please write to hello@tabithastudio.com with a description of what happened and, if possible, which step you were on. We respond within two business days. For urgent matters involving a purchase, please mention that in the subject line.

QUESTIONS DURING THE PROCESS:
Customers are always welcome to ask here — that is what Tabitha is for. For questions about a specific order or account, hello@tabithastudio.com is the best route, and the team responds within two business days.

AI MODELS USED:
Tabitha Studio uses Google Imagen for design renders and Anthropic Claude for the written content in Tech Packs — including the design narrative, construction specifications, fabric notes, and pattern drafting guidance. Both are configured to protect customer data and not to use it for training.

DATA PRIVACY & SECURITY:
Customer measurements and personal data are stored securely with row-level security — each user can only access their own data. Accounts are authenticated via Google Sign-In; Tabitha Studio never stores passwords. Measurements are not included in AI prompts — only design preference selections are shared with AI providers. Neither provider uses customer data for their own training. Full details are available in the Privacy Policy at tabithastudio.com/privacy.html.`;


// ─── One message bubble ───────────────────────────────────────────────────────
function Bubble({ role, text, avatarSrc }) {
  const isUser = role === "user";
  return (
    <div style={{
      display: "flex",
      justifyContent: isUser ? "flex-end" : "flex-start",
      marginBottom: 10,
    }}>
      {!isUser && (
        <div style={{
          width: 26, height: 26, borderRadius: "50%",
          background: ROS, flexShrink: 0,
          overflow: "hidden",
          marginRight: 8, marginTop: 2,
          border: `1px solid ${ROS}`,
        }}>
          {avatarSrc ? (
            <img src={avatarSrc} alt="Tabitha" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 8%", display: "block" }} />
          ) : (
            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", fontFamily: "Georgia, serif" }}>T</div>
          )}
        </div>
      )}
      <div style={{
        maxWidth: "75%",
        background: isUser ? C : "#fff",
        color: isUser ? "#fff" : C,
        borderRadius: isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
        padding: "10px 14px",
        fontSize: 13,
        lineHeight: 1.6,
        fontFamily: "system-ui, sans-serif",
        boxShadow: isUser ? "none" : "0 1px 4px rgba(0,0,0,0.07)",
        border: isUser ? "none" : `1px solid ${BRD}`,
        whiteSpace: "pre-wrap",
      }}>
        {text}
      </div>
    </div>
  );
}

// ─── Typing indicator ─────────────────────────────────────────────────────────
function TypingDots({ avatarSrc }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
      <div style={{
        width: 26, height: 26, borderRadius: "50%",
        background: ROS, flexShrink: 0,
        overflow: "hidden",
        border: `1px solid ${ROS}`,
      }}>
        {avatarSrc ? (
          <img src={avatarSrc} alt="Tabitha" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 8%", display: "block" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", fontFamily: "Georgia, serif" }}>T</div>
        )}
      </div>
      <div style={{
        background: "#fff",
        border: `1px solid ${BRD}`,
        borderRadius: "18px 18px 18px 4px",
        padding: "10px 16px",
        display: "flex", gap: 5, alignItems: "center",
      }}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{
            width: 6, height: 6, borderRadius: "50%",
            background: ROS, opacity: 0.7,
            animation: "tabithaBounce 1.2s infinite",
            animationDelay: `${i * 0.2}s`,
          }} />
        ))}
      </div>
      <style>{`
        @keyframes tabithaBounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-5px); }
        }
      `}</style>
    </div>
  );
}

// ─── Suggested prompts ────────────────────────────────────────────────────────
const ALL_SUGGESTIONS = [
  "Why use Tabitha Studio?",
  "What are the steps involved?",
  "How much does this cost?",
  "What do I get at the end?",
  "Do I need a subscription?",
  "What's in a Tech Pack?",
  "Can I use any dressmaker?",
  "What about fabric and yardage?",
  "How do I take my measurements?",
  "What if I don't like my design?",
  "Do I need to find a maker first?",
  "Will my Tech Pack be saved?",
  "Is my information kept private?",
  "Who is behind Tabitha?",
  "How do I use a gallery template?",
];

function getRotatedSuggestions(count = 4) {
  const shuffled = [...ALL_SUGGESTIONS].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

// ─── Main chat widget ─────────────────────────────────────────────────────────
// avatarSrc: pass your model image, e.g. import avatarSrc from "./Tabitha_-_Vector.png"
// apiKey: pass process.env.REACT_APP_ANTHROPIC_API_KEY from your environment
export default function TabithaChat({ avatarSrc, apiKey }) {
  const [open, setOpen]         = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [suggestions, setSuggestions] = useState(() => getRotatedSuggestions());
  const bottomRef               = useRef(null);
  const inputRef                = useRef(null);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, open]);

  // Focus input when opened
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  function handleOpen() {
    setOpen(true);
    if (!hasOpened) {
      setHasOpened(true);
      // Greeting on first open
      setMessages([{
        role: "assistant",
        text: "Hello — I'm Tabitha. Whether you have a question about the Tech Pack, the design process, fabric, or anything else, I'm happy to help. What would you like to know?",
      }]);
    }
  }

  async function send(text) {
    const trimmed = (text || input).trim();
    if (!trimmed || loading) return;
    setInput("");

    const newMessages = [...messages, { role: "user", text: trimmed }];
    setMessages(newMessages);
    setLoading(true);

    try {
      const apiMessages = newMessages.map(m => ({
        role: m.role,
        content: m.text,
      }));

      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(apiKey ? {
            "x-api-key": apiKey,
            "anthropic-version": "2023-06-01",
            "anthropic-beta": "prompt-caching-2024-07-31",
            "anthropic-dangerous-direct-browser-access": "true",
          } : {}),
        },
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 1000,
          system: [
            {
              type: "text",
              text: SYSTEM_PROMPT,
              cache_control: { type: "ephemeral" },
            },
          ],
          messages: apiMessages,
        }),
      });

      const data = await response.json();
      const reply = data?.content?.[0]?.text || "I'm sorry, I couldn't reach the studio just now. Please try again in a moment, or write to us at hello@tabithastudio.com.";

      setMessages(prev => [...prev, { role: "assistant", text: reply }]);
      setSuggestions(getRotatedSuggestions());
    } catch {
      setMessages(prev => [...prev, {
        role: "assistant",
        text: "My apologies — something went wrong on my end. Please try again, or reach us directly at hello@tabithastudio.com.",
      }]);
    } finally {
      setLoading(false);
    }
  }

  function handleKey(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  const showSuggestions = false; // replaced by per-response rotation

  return (
    <>
      {/* ── Floating button ── */}
      {!open && (
        <button
          onClick={handleOpen}
          aria-label="Chat with Tabitha"
          style={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 9999,
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: avatarSrc ? "transparent" : C,
            border: `2.5px solid ${ROS}`,
            cursor: "pointer",
            padding: 0,
            overflow: "visible",
            boxShadow: "0 4px 20px rgba(0,0,0,0.22)",
            transition: "transform 0.15s ease, box-shadow 0.15s ease",
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.06)"; e.currentTarget.style.boxShadow = "0 6px 28px rgba(0,0,0,0.28)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.22)"; }}
        >
          {avatarSrc ? (
            <img
              src={avatarSrc}
              alt="Tabitha"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center 8%",
                display: "block",
                borderRadius: "50%",
                clipPath: "circle(50%)",
              }}
            />
          ) : (
            <span style={{ fontSize: 20, fontWeight: 700, color: ROS, fontFamily: "Georgia, serif" }}>T</span>
          )}
          {/* Label pill */}
          <div style={{
            position: "absolute",
            right: 70,
            top: "50%",
            transform: "translateY(-50%)",
            background: C,
            color: "#fff",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.1em",
            padding: "5px 12px",
            borderRadius: 999,
            whiteSpace: "nowrap",
            fontFamily: "system-ui, sans-serif",
            boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
            pointerEvents: "none",
          }}>
            Ask Tabitha
          </div>
        </button>
      )}

      {/* ── Chat panel ── */}
      {open && (
        <div style={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 9999,
          width: 360,
          maxWidth: "calc(100vw - 32px)",
          height: 520,
          maxHeight: "calc(100vh - 48px)",
          borderRadius: 20,
          background: BG,
          border: `1px solid ${BRD}`,
          boxShadow: "0 8px 48px rgba(0,0,0,0.18)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          fontFamily: "system-ui, sans-serif",
          animation: "tabithaSlideUp 0.2s ease",
        }}>
          <style>{`
            @keyframes tabithaSlideUp {
              from { opacity: 0; transform: translateY(12px) scale(0.97); }
              to   { opacity: 1; transform: translateY(0) scale(1); }
            }
          `}</style>

          {/* Header */}
          <div style={{
            background: C,
            padding: "14px 18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: "50%",
                border: `1.5px solid ${ROS}`,
                overflow: "hidden",
                flexShrink: 0,
                background: ROS,
              }}>
                {avatarSrc ? (
                  <img src={avatarSrc} alt="Tabitha" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 8%", display: "block" }} />
                ) : (
                  <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: "#fff", fontFamily: "Georgia, serif" }}>T</div>
                )}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", letterSpacing: "0.02em" }}>Tabitha</div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", letterSpacing: "0.15em", textTransform: "uppercase", marginTop: 1 }}>Studio · Always here</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} style={{
              background: "none", border: "none", cursor: "pointer",
              color: "rgba(255,255,255,0.5)", padding: 4, borderRadius: 6,
              fontSize: 18, lineHeight: 1, display: "flex", alignItems: "center",
            }}
              onMouseEnter={e => e.currentTarget.style.color = "#fff"}
              onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.5)"}
            >✕</button>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1,
            overflowY: "auto",
            padding: "16px 14px 8px",
            display: "flex",
            flexDirection: "column",
          }}>
            {messages.map((m, i) => <Bubble key={i} role={m.role} text={m.text} avatarSrc={avatarSrc} />)}
            {loading && <TypingDots avatarSrc={avatarSrc} />}

            {/* Suggested questions — shown on open and after every reply */}
            {!loading && messages.length > 0 && messages[messages.length - 1].role === "assistant" && (
              <div style={{ marginTop: 6, marginBottom: 4 }}>
                <div style={{ fontSize: 10, color: "#a8a29e", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 8, paddingLeft: 34 }}>
                  {messages.length <= 1 ? "Common questions" : "You might also ask"}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, paddingLeft: 34 }}>
                  {suggestions.map(s => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      style={{
                        background: "#fff",
                        border: `1px solid ${BRD}`,
                        borderRadius: 999,
                        padding: "5px 12px",
                        fontSize: 11,
                        color: C,
                        cursor: "pointer",
                        fontFamily: "system-ui, sans-serif",
                        transition: "background 0.1s, border-color 0.1s",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = "#EDE8E3"; e.currentTarget.style.borderColor = ROS; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = BRD; }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: "10px 12px 12px",
            borderTop: `1px solid ${BRD}`,
            background: "#fff",
            flexShrink: 0,
            display: "flex",
            gap: 8,
            alignItems: "flex-end",
          }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Ask me anything…"
              rows={1}
              style={{
                flex: 1,
                resize: "none",
                border: `1px solid ${BRD}`,
                borderRadius: 12,
                padding: "9px 12px",
                fontSize: 13,
                fontFamily: "system-ui, sans-serif",
                color: C,
                background: BG,
                outline: "none",
                lineHeight: 1.5,
                maxHeight: 80,
                overflowY: "auto",
              }}
              onFocus={e => e.target.style.borderColor = ROS}
              onBlur={e => e.target.style.borderColor = BRD}
            />
            <button
              onClick={() => send()}
              disabled={!input.trim() || loading}
              style={{
                width: 36, height: 36,
                borderRadius: "50%",
                background: !input.trim() || loading ? BRD : C,
                border: "none",
                cursor: !input.trim() || loading ? "default" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                transition: "background 0.15s",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 2L11 13" /><path d="M22 2L15 22 11 13 2 9l20-7z" />
              </svg>
            </button>
          </div>

          {/* Footer note */}
          <div style={{
            padding: "6px 14px 8px",
            background: "#fff",
            fontSize: 9,
            color: "#c4bfba",
            textAlign: "center",
            letterSpacing: "0.08em",
            fontFamily: "system-ui, sans-serif",
          }}>
            Tabitha Studio · hello@tabithastudio.com
          </div>
        </div>
      )}
    </>
  );
}
